import { InstalledUpiApp } from '../installedUpiIntents'
import { isBlockedUpiApp, resolveUpiAppDisplay } from './upiAppConfig'
import { InvestPaymentMethodType, InvestPaymentMethodTypes } from '../../types/mf-execution.types'

/** True when a UPI app must not appear in buy/SIP payment method pickers. */
export function isExcludedPaymentUpiApp(packageName: string | null | undefined): boolean {
  return isBlockedUpiApp(packageName)
}

/** Subset of native-installed apps eligible for one-time payment UPI picker. */
export function filterInstalledUpiAppsForPayment(
  installedUpiApps: InstalledUpiApp[] | null | undefined
): InstalledUpiApp[] {
  return (installedUpiApps ?? []).filter((app) => !isExcludedPaymentUpiApp(app?.packageName))
}

/** Merges bank account details into a UPI row and marks it `UPI_INTENT` with the chosen package name. */
export function applyBankAccountToUpiMethod(
  baseMethod: InvestPaymentMethodType,
  bankAccount: InvestPaymentMethodType | null,
  upiPackageName: string | null
): InvestPaymentMethodType {
  if (!bankAccount) {
    return {
      ...baseMethod,
      type: InvestPaymentMethodTypes.UPI_INTENT,
      upiPackageName: upiPackageName ?? baseMethod?.upiPackageName ?? null
    }
  }

  return {
    ...baseMethod,
    type: InvestPaymentMethodTypes.UPI_INTENT,
    upiPackageName: upiPackageName ?? baseMethod?.upiPackageName ?? null,
    displayAccNumber: bankAccount?.displayAccNumber ?? baseMethod?.displayAccNumber ?? '',
    accountNumber: bankAccount?.accountNumber ?? baseMethod?.accountNumber,
    ifscCode: bankAccount?.ifscCode ?? baseMethod?.ifscCode,
    logoUrl: bankAccount?.logoUrl ?? baseMethod?.logoUrl
  }
}

export function findMatchedUpiMethod(app: InstalledUpiApp, paymentMethods: InvestPaymentMethodType[] | undefined) {
  const appPackageName = app?.packageName?.toLowerCase() ?? ''
  const appName = app?.name?.toLowerCase() ?? ''

  return paymentMethods?.find((method) => {
    if (method?.type !== InvestPaymentMethodTypes.UPI) return false

    const methodPackageName = method?.upiPackageName?.toLowerCase() ?? ''
    const methodUpiId = method?.upiId?.toLowerCase() ?? ''

    if (methodPackageName && (methodPackageName === appPackageName || appPackageName.includes(methodPackageName))) {
      return true
    }

    if (methodUpiId && (methodUpiId === appPackageName || appPackageName.includes(methodUpiId))) {
      return true
    }

    if (appName && methodPackageName && appName.includes(methodPackageName)) {
      return true
    }

    return false
  })
}

/**
 * Package id for `/order/otp` (`upiPackageName`). Uses native package fields only — never app display name.
 */
export function resolveUpiPackageName(app: InstalledUpiApp, baseMethod?: InvestPaymentMethodType | null) {
  const appRecord = app as unknown as Record<string, unknown>
  const packageName =
    (typeof appRecord?.packageName === 'string' ? appRecord.packageName : '') ||
    (typeof appRecord?.package === 'string' ? appRecord.package : '') ||
    (typeof appRecord?.package_name === 'string' ? appRecord.package_name : '') ||
    ''

  return packageName.trim() || baseMethod?.upiPackageName?.trim() || null
}

export function resolveBankAccountForUpi(
  bankAccount: InvestPaymentMethodType | null,
  bankAccountDetails?: InvestPaymentMethodType[]
) {
  if (bankAccount) return bankAccount
  if (!bankAccountDetails?.length) return null
  return bankAccountDetails[0] ?? null
}

export function getUpiAppSelectionId(app: InstalledUpiApp) {
  return `upi:${app?.packageName ?? app?.name ?? ''}`
}

/** First app in the native list, or the one matching the backend default package when set. */
export function getDefaultInstalledUpiApp(
  installedUpiApps: InstalledUpiApp[] | null | undefined,
  defaultPaymentMethodDetails?: InvestPaymentMethodType | null
): InstalledUpiApp | null {
  if (!installedUpiApps?.length) return null

  const defaultPackageName = defaultPaymentMethodDetails?.upiPackageName?.toLowerCase() ?? ''
  if (defaultPackageName) {
    const matchedDefault = installedUpiApps.find((app) => {
      const packageName = app?.packageName?.toLowerCase() ?? ''
      const appName = app?.name?.toLowerCase() ?? ''
      return packageName.includes(defaultPackageName) || appName.includes(defaultPackageName)
    })
    if (matchedDefault) return matchedDefault
  }

  return installedUpiApps[0] ?? null
}

/** Resolves the installed app row for a selected `upiPackageName` (used for amount-bar label and icon). */
export function findInstalledUpiAppByPackage(
  installedUpiApps: InstalledUpiApp[] | null | undefined,
  packageName: string | null | undefined
): InstalledUpiApp | null {
  const normalizedPackageName = packageName?.toLowerCase() ?? ''
  if (!normalizedPackageName || !installedUpiApps?.length) return null

  return (
    installedUpiApps.find((app) => {
      const appPackageName = app?.packageName?.toLowerCase() ?? ''
      const appName = app?.name?.toLowerCase() ?? ''
      return (
        appPackageName === normalizedPackageName ||
        appPackageName.includes(normalizedPackageName) ||
        appName.includes(normalizedPackageName)
      )
    }) ?? null
  )
}

export function getNetBankingSelectionId(method: InvestPaymentMethodType) {
  return `net-banking:${method?.paymentMethodId ?? method?.displayAccNumber ?? ''}`
}

/** First net-banking row from the invest payment-methods API (amount-screen default when no UPI intents). */
export function findNetBankingPaymentMethod(paymentMethods: InvestPaymentMethodType[] | undefined) {
  return paymentMethods?.find((method) => method?.type === InvestPaymentMethodTypes.NET_BANKING) ?? null
}

/** First linked UPI row from the API (fallback when intents are unavailable). */
export function findFirstUpiPaymentMethod(paymentMethods: InvestPaymentMethodType[] | undefined) {
  return paymentMethods?.find((method) => method?.type === InvestPaymentMethodTypes.UPI) ?? null
}

/** Client-only type: native app + API payment method id, sent as UPI_INTENT on order/OTP. */
export function isUpiIntentPaymentMethod(paymentMethod: Pick<InvestPaymentMethodType, 'type'> | null | undefined) {
  return paymentMethod?.type === InvestPaymentMethodTypes.UPI_INTENT
}

/** Amount ceiling for UPI intent flows from `PaymentDetailsResponse.upiIntentMaximumAllowedAmount`. */
export function isAmountWithinUpiIntentLimit(amount: number, upiIntentMaximumAllowedAmount: number | null | undefined) {
  if (!upiIntentMaximumAllowedAmount) return true
  return amount <= upiIntentMaximumAllowedAmount
}

type PaymentMethodAmountLimitOptions = {
  upiIntentMaximumAllowedAmount?: number | null
  hasInstalledUpiIntents?: boolean
}

/** Validates amount against per-method max, or UPI intent max when the method is/will be paid via intent. */
export function isPaymentMethodValidForAmount(
  paymentMethod: InvestPaymentMethodType | null | undefined,
  amount: number,
  options: PaymentMethodAmountLimitOptions = {}
) {
  if (!paymentMethod) return false

  const usesUpiIntentLimit =
    isUpiIntentPaymentMethod(paymentMethod) ||
    (options?.hasInstalledUpiIntents && paymentMethod?.type === InvestPaymentMethodTypes.UPI)

  if (usesUpiIntentLimit) {
    return isAmountWithinUpiIntentLimit(amount, options?.upiIntentMaximumAllowedAmount)
  }

  if (!paymentMethod?.maximumAllowedAmount) return true
  return amount <= (paymentMethod?.maximumAllowedAmount ?? Infinity)
}

export function getInitialSelectedBankAccount(
  bankAccountDetails: InvestPaymentMethodType[] | undefined,
  defaultPaymentMethodDetails: InvestPaymentMethodType | null | undefined
) {
  const accounts = bankAccountDetails ?? []
  if (!accounts.length) return null

  const defaultPaymentMethodId = defaultPaymentMethodDetails?.paymentMethodId
  if (defaultPaymentMethodId) {
    const matchedDefault = accounts.find((account) => account?.paymentMethodId === defaultPaymentMethodId)
    if (matchedDefault) return matchedDefault
  }

  return accounts[0] ?? null
}

/**
 * Builds a `UPI_INTENT` selection from an installed app plus API payment methods / bank accounts.
 * Reuses the matched UPI method’s limits and id; attaches `upiPackageName` for native redirect.
 */
export function buildUpiIntentPaymentMethod(
  app: InstalledUpiApp,
  paymentMethods: InvestPaymentMethodType[] | undefined,
  bankAccount: InvestPaymentMethodType | null,
  bankAccountDetails?: InvestPaymentMethodType[]
): InvestPaymentMethodType | null {
  const resolvedBank = resolveBankAccountForUpi(bankAccount, bankAccountDetails)
  const baseMethod =
    findMatchedUpiMethod(app, paymentMethods) ??
    paymentMethods?.find((method) => method?.type === InvestPaymentMethodTypes.UPI) ??
    null

  if (baseMethod?.paymentMethodId) {
    return applyBankAccountToUpiMethod(
      baseMethod,
      bankAccountDetails?.length ? resolvedBank : null,
      resolveUpiPackageName(app, baseMethod)
    )
  }

  if (!resolvedBank?.paymentMethodId) return null

  return applyBankAccountToUpiMethod(
    {
      paymentMethodId: resolvedBank.paymentMethodId,
      type: InvestPaymentMethodTypes.UPI,
      displayAccNumber: resolvedBank.displayAccNumber ?? '',
      maximumAllowedAmount: resolvedBank.maximumAllowedAmount,
      accountNumber: resolvedBank.accountNumber,
      ifscCode: resolvedBank.ifscCode,
      logoUrl: resolvedBank.logoUrl,
      upiId: resolvedBank.upiId
    },
    bankAccountDetails?.length ? resolvedBank : null,
    resolveUpiPackageName(app, null)
  )
}

export type ResolvePayablePaymentMethodParams = {
  amount: number
  payablePaymentMethod: InvestPaymentMethodType | null
  selectedOptionId: string | null
  selectedBankAccount: InvestPaymentMethodType | null
  installedUpiApps: InstalledUpiApp[] | null | undefined
  paymentMethods: InvestPaymentMethodType[] | undefined
  bankAccountDetails?: InvestPaymentMethodType[]
  defaultPaymentMethodDetails?: InvestPaymentMethodType | null
  upiIntentMaximumAllowedAmount?: number | null
}

export function buildUpiIntentForSelection(
  selectedOptionId: string,
  installedUpiApps: InstalledUpiApp[],
  paymentMethods: InvestPaymentMethodType[] | undefined,
  selectedBankAccount: InvestPaymentMethodType | null,
  bankAccountDetails?: InvestPaymentMethodType[],
  defaultPaymentMethodDetails?: InvestPaymentMethodType | null
): InvestPaymentMethodType | null {
  const selectedApp = installedUpiApps.find((app) => getUpiAppSelectionId(app) === selectedOptionId)
  if (!selectedApp) return null

  const bankAccount =
    selectedBankAccount ?? getInitialSelectedBankAccount(bankAccountDetails, defaultPaymentMethodDetails)

  return buildUpiIntentPaymentMethod(selectedApp, paymentMethods, bankAccount, bankAccountDetails)
}

export type ResolveConfirmablePaymentMethodParams = Omit<
  ResolvePayablePaymentMethodParams,
  'amount' | 'upiIntentMaximumAllowedAmount'
>

function resolvePaymentMethodFromSelection({
  amount,
  payablePaymentMethod,
  selectedOptionId,
  selectedBankAccount,
  installedUpiApps,
  paymentMethods,
  bankAccountDetails,
  defaultPaymentMethodDetails,
  upiIntentMaximumAllowedAmount,
  requireAmount
}: ResolvePayablePaymentMethodParams & { requireAmount: boolean }): InvestPaymentMethodType | null {
  if (requireAmount && !amount) return null

  if (selectedOptionId?.startsWith('upi:') && installedUpiApps?.length) {
    const upiAllowedForAmount =
      !requireAmount || isAmountWithinUpiIntentLimit(amount ?? 0, upiIntentMaximumAllowedAmount)

    if (upiAllowedForAmount) {
      const upiIntentMethod = buildUpiIntentForSelection(
        selectedOptionId,
        installedUpiApps,
        paymentMethods,
        selectedBankAccount,
        bankAccountDetails,
        defaultPaymentMethodDetails
      )
      if (upiIntentMethod?.paymentMethodId) {
        return normalizePaymentMethodForOrder(upiIntentMethod)
      }
      return null
    }
  }

  if (selectedOptionId?.startsWith('net-banking:') && paymentMethods?.length) {
    const netBankingMethod = paymentMethods.find((method) => getNetBankingSelectionId(method) === selectedOptionId)
    if (netBankingMethod?.paymentMethodId) {
      return normalizePaymentMethodForOrder(netBankingMethod)
    }
    return null
  }

  if (payablePaymentMethod?.paymentMethodId) {
    return normalizePaymentMethodForOrder(payablePaymentMethod)
  }

  return null
}

export function resolvePayablePaymentMethod(params: ResolvePayablePaymentMethodParams): InvestPaymentMethodType | null {
  return resolvePaymentMethodFromSelection({ ...params, requireAmount: true })
}

export function resolveConfirmablePaymentMethod(
  params: ResolveConfirmablePaymentMethodParams
): InvestPaymentMethodType | null {
  return resolvePaymentMethodFromSelection({ ...params, amount: 0, requireAmount: false })
}

export function refreshSelectedPaymentMethodAfterFetch(
  paymentMethods: InvestPaymentMethodType[],
  current: InvestPaymentMethodType
): InvestPaymentMethodType | null {
  if (current?.type === InvestPaymentMethodTypes.UPI_INTENT) {
    const upiMethod =
      paymentMethods.find(
        (method) =>
          method?.type === InvestPaymentMethodTypes.UPI && method?.paymentMethodId === current?.paymentMethodId
      ) ?? paymentMethods.find((method) => method?.type === InvestPaymentMethodTypes.UPI)

    if (!upiMethod) return null

    return {
      ...upiMethod,
      type: InvestPaymentMethodTypes.UPI_INTENT,
      upiPackageName: current?.upiPackageName ?? upiMethod?.upiPackageName ?? null,
      displayAccNumber: current?.displayAccNumber ?? upiMethod?.displayAccNumber ?? '',
      accountNumber: current?.accountNumber ?? upiMethod?.accountNumber,
      ifscCode: current?.ifscCode ?? upiMethod?.ifscCode,
      logoUrl: current?.logoUrl ?? upiMethod?.logoUrl
    }
  }

  return paymentMethods.find((method) => method?.paymentMethodId === current?.paymentMethodId) ?? null
}

/**
 * Maps the client selection to the type sent on `/order/otp`. Any non-empty `upiPackageName` is UPI_INTENT.
 */
export function getOrderPaymentMethodType(
  paymentMethod: Pick<InvestPaymentMethodType, 'type' | 'upiPackageName'> | null | undefined
): `${InvestPaymentMethodTypes}` {
  if (paymentMethod?.upiPackageName?.trim()) {
    return InvestPaymentMethodTypes.UPI_INTENT
  }

  const type = paymentMethod?.type

  if (type === InvestPaymentMethodTypes.UPI_INTENT) {
    return InvestPaymentMethodTypes.UPI_INTENT
  }

  if (type === InvestPaymentMethodTypes.NET_BANKING) {
    return InvestPaymentMethodTypes.NET_BANKING
  }

  if (type === InvestPaymentMethodTypes.MANDATE) {
    return InvestPaymentMethodTypes.MANDATE
  }

  return type ?? InvestPaymentMethodTypes.UPI
}

export function normalizePaymentMethodForOrder(paymentMethod: InvestPaymentMethodType): InvestPaymentMethodType {
  return {
    ...paymentMethod,
    type: getOrderPaymentMethodType(paymentMethod)
  }
}

export function isPayCtaEnabled(params: ResolvePayablePaymentMethodParams): boolean {
  return resolvePayablePaymentMethod(params) !== null
}

export function isConfirmCtaEnabled(params: ResolveConfirmablePaymentMethodParams): boolean {
  return resolveConfirmablePaymentMethod(params) !== null
}

export type UpiIntentDisplay = {
  appName: string
  appLogoUrl: string
}

export function getUpiIntentDisplayForPaymentMethod(
  paymentMethod: Pick<InvestPaymentMethodType, 'type' | 'upiPackageName' | 'upiId'> | null | undefined,
  installedUpiApps: InstalledUpiApp[] | null | undefined
): UpiIntentDisplay {
  if (!isUpiIntentPaymentMethod(paymentMethod)) {
    return {
      appName: paymentMethod?.upiId ?? '',
      appLogoUrl: ''
    }
  }

  const matchedApp = findInstalledUpiAppByPackage(installedUpiApps, paymentMethod?.upiPackageName)
  const resolved = resolveUpiAppDisplay(paymentMethod?.upiPackageName, {
    name: matchedApp?.name ?? paymentMethod?.upiId,
    logoUrl: matchedApp?.appLogoUrl
  })

  return {
    appName: resolved.name,
    appLogoUrl: resolved.logoUrl
  }
}

export type OrderOtpPaymentFields = {
  paymentMethodType: `${InvestPaymentMethodTypes}`
  upiPackageName?: string
}

export function buildOrderOtpPaymentFields(
  paymentMethod: Pick<InvestPaymentMethodType, 'type' | 'upiPackageName'> | null | undefined,
  upiIntentsEnabled: boolean
): OrderOtpPaymentFields {
  if (!paymentMethod) {
    return { paymentMethodType: InvestPaymentMethodTypes.UPI }
  }

  if (upiIntentsEnabled) {
    const paymentMethodType = getOrderPaymentMethodType(paymentMethod)
    const upiPackageName = paymentMethod?.upiPackageName?.trim()

    if (upiPackageName) {
      return { paymentMethodType, upiPackageName }
    }

    return { paymentMethodType }
  }

  return {
    paymentMethodType: (paymentMethod?.type ?? InvestPaymentMethodTypes.UPI) as `${InvestPaymentMethodTypes}`
  }
}
